import { Divider, Field, Flex, TextButton } from '@procurify/ui'
import { useSelector } from 'react-redux'

import { DepartmentSelect } from '@webapp/components/DepartmentSelect'
import { type FilterManagerContextValues } from '@webapp/components/FilterManager/FilterManagerContext'
import { FiltersPopover } from '@webapp/components/FiltersPopover'
import { SavedFilterManager } from '@webapp/components/SavedFilterManager'
import { UserSelect } from '@webapp/components/UserSelect'
import { RequestTypes } from '@webapp/core/entities/internal/enums/RequestTypeEnum'
import { useQueryAllOrderItemCustomFields } from '@webapp/core/features/get-custom-fields/models/useQueryAllOrderItemCustomFields'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import { type IBudgetCategoryListViewFilters } from '@webapp/hooks/useBudgetCategoryListFilterManager'
import { type SavedFilterCollectionKeys } from '@webapp/hooks/useSavedFilterManager/types'
import { BudgetCategoryTypes } from '@webapp/models/enums/BudgetCategoryType'
import {
  BudgetCustomFieldCondition,
  type IBudgetCustomFieldConditionValue,
} from '@webapp/platform/budgets'
import { Permissions } from '@webapp/platform/iam'
import { BudgetCategoryListSavedFilterManager } from './components/BudgetCategoryListSavedFilterManager'
import { useBudgetCategoryListCustomFieldFilterRows } from './hooks/useBudgetCategoryListCustomFieldFilterRows'
import messages from './messages'
import { AccountCodeSelect } from '../../AccountCodeSelect'
import { BudgetCategoryTypeSelect } from '../BudgetCategoryTypeSelect'
import { type BudgetCategoryTypeOption } from '../BudgetCategoryTypeSelect/BudgetCategoryTypeSelect'
import { useBudgetCategoryTypeOptions } from '../BudgetCategoryTypeSelect/hooks/useBudgetCategoryTypeOptions'

const FILTER_ITEM_MIN_WIDTH = 360

export interface IBudgetCategoriesViewFilterButtonProps {
  collectionId: keyof Pick<
    SavedFilterCollectionKeys,
    'settings.manage.budget.category' | 'dashboard.budget.category.list'
  >
  filterManager: FilterManagerContextValues<IBudgetCategoryListViewFilters>
  shouldHideDepartmentOptionsForRequesters?: boolean
  onFilterChange?: () => undefined | void
}

export const BudgetCategoryListFilterButton = ({
  collectionId,
  filterManager,
  shouldHideDepartmentOptionsForRequesters = false,
  onFilterChange = () => undefined,
}: IBudgetCategoriesViewFilterButtonProps) => {
  const intl = useProcurifyIntl()
  const { enable_flexible_spend_tracking: isFlexibleSpendTrackingEnabled } =
    useSelector(makeSelectFeatureSwitches())
  const PUR_4252_BUDGET_BY_CUSTOM_FIELDS = useFeatureFlag(
    'PUR_4252_BUDGET_BY_CUSTOM_FIELDS'
  )

  const budgetCategoryTypeOptions = useBudgetCategoryTypeOptions()

  const canSaveCustomFieldFilters =
    PUR_4252_BUDGET_BY_CUSTOM_FIELDS &&
    collectionId === 'settings.manage.budget.category'

  // A saved filter is decoded with the field list, so applying one before the
  // list loads would drop its custom field filters.
  const {
    customFields: savableCustomFields,
    isLoading: isLoadingSavableCustomFields,
  } = useQueryAllOrderItemCustomFields({
    enabled: canSaveCustomFieldFilters,
  })

  const { filterValues, clearFilters, addFilter, removeFilter } = filterManager

  const {
    rows: customFieldFilterRows,
    changeRow: changeCustomFieldFilterRow,
    clearRows: clearCustomFieldFilterRows,
  } = useBudgetCategoryListCustomFieldFilterRows({
    filterManager,
    onFilterChange,
  })

  const USER_SELECT_PERMISSIONS_FILTER = [
    Permissions.APPROVE_EXPENSEREPORT,
    Permissions.APPROVE_ORDER,
    Permissions.APPROVE_PAY_REQUEST,
  ]

  const onBudgetCategoryTypeChange = (option: BudgetCategoryTypeOption) => {
    const paramValue = option.value

    if (paramValue) {
      addFilter({
        key: 'source_of_truth',
        paramValue,
        value: option,
      })
    } else {
      removeFilter({
        key: 'source_of_truth',
        paramValue: filterValues.source_of_truth?.value,
        value: filterValues.source_of_truth,
      })
    }

    onFilterChange()
  }

  const renderCustomFieldFilterRow = (
    customFieldFilterRow: IBudgetCustomFieldConditionValue,
    rowIndex: number
  ) => {
    const otherRowFieldIds = customFieldFilterRows
      .filter(
        (otherRow, otherRowIndex) =>
          otherRowIndex !== rowIndex && otherRow.field
      )
      .map((otherRow) => otherRow.field.id)

    return (
      // Without a set width, the column row measures this item while the
      // selected values wrap more, and leaves a gap below the row.
      <Flex.Item key={rowIndex} width={1} minWidth={FILTER_ITEM_MIN_WIDTH}>
        <BudgetCustomFieldCondition
          id={`custom-field-filter-${rowIndex}`}
          value={customFieldFilterRow}
          onChange={(nextRow) => changeCustomFieldFilterRow(rowIndex, nextRow)}
          fieldLabel={intl.formatMessage(messages.customFieldLabel, {
            number: rowIndex + 1,
          })}
          valuesLabel={intl.formatMessage(messages.customFieldValuesLabel)}
          excludedFieldIds={otherRowFieldIds}
        />
      </Flex.Item>
    )
  }

  const budgetTypeLabel = intl.formatMessage(
    isFlexibleSpendTrackingEnabled
      ? messages.budgetType
      : messages.budgetCategoryType
  )

  return (
    <BudgetCategoryListSavedFilterManager
      collectionId={collectionId}
      budgetCategoryTypeOptions={budgetCategoryTypeOptions}
      filterManager={filterManager}
      savableCustomFields={canSaveCustomFieldFilters ? savableCustomFields : []}
    >
      <FiltersPopover>
        {({ dismiss }) => (
          <>
            <Field
              label={intl.formatMessage(messages.saveFilterButtonLabel)}
              labelFor='saved-filter'
            >
              <SavedFilterManager.Select
                inputId='saved-filter'
                isDisabled={
                  canSaveCustomFieldFilters && isLoadingSavableCustomFields
                }
                onEdit={dismiss}
              />
            </Field>

            <Divider />

            {collectionId !== 'dashboard.budget.category.list' && (
              <>
                <Flex.Row>
                  <Flex.Item width={1 / 2} minWidth={FILTER_ITEM_MIN_WIDTH}>
                    <Field
                      label={intl.formatMessage(messages.viewingAccess)}
                      labelFor='viewing-access-filter'
                    >
                      <UserSelect
                        inputId='viewing-access-filter'
                        name={intl.formatMessage(messages.viewingAccess)}
                        defaultOptions={false}
                        permissions={USER_SELECT_PERMISSIONS_FILTER}
                        value={filterValues.users}
                        onChange={(value) => {
                          addFilter({
                            key: 'users',
                            paramValue: value.id,
                            value,
                          })
                          onFilterChange()
                        }}
                      />
                    </Field>
                  </Flex.Item>

                  <Flex.Item width={1 / 2} minWidth={FILTER_ITEM_MIN_WIDTH}>
                    <Field
                      label={budgetTypeLabel}
                      labelFor='budget-category-type-filter'
                    >
                      <BudgetCategoryTypeSelect
                        name={budgetTypeLabel}
                        inputId='budget-category-type-filter'
                        onChange={(value) => onBudgetCategoryTypeChange(value)}
                        value={filterValues.source_of_truth}
                        defaultParam={BudgetCategoryTypes.ALL}
                      />
                    </Field>
                  </Flex.Item>
                </Flex.Row>

                <Divider />
              </>
            )}

            <Flex.Row flexDirection='column'>
              <Flex.Item minWidth={FILTER_ITEM_MIN_WIDTH}>
                <Field
                  label={intl.formatMessage(messages.departments)}
                  labelFor='departments-filter'
                >
                  <DepartmentSelect
                    inputId='departments-filter'
                    name={intl.formatMessage(messages.departments)}
                    value={filterValues.departments}
                    getOptionLabel={(department) =>
                      `${department?.branch?.name} - ${department.name}`
                    }
                    params={
                      !shouldHideDepartmentOptionsForRequesters
                        ? { requestable: RequestTypes.ORDER }
                        : null
                    }
                    onChange={(value) => {
                      addFilter({
                        key: 'departments',
                        paramValue: value.id,
                        value,
                      })
                      onFilterChange()
                    }}
                  />
                </Field>
              </Flex.Item>

              <Flex.Item minWidth={FILTER_ITEM_MIN_WIDTH}>
                <Field
                  label={intl.formatMessage(messages.accountCodes)}
                  labelFor='account-code-filter'
                >
                  <AccountCodeSelect
                    inputId='account-code-filter'
                    name={intl.formatMessage(messages.accountCodes)}
                    defaultOptions={false}
                    value={filterValues.account_codes}
                    getOptionLabel={(accCode) =>
                      `${accCode.code} - ${accCode.description}`
                    }
                    onChange={(value) => {
                      addFilter({
                        key: 'account_codes',
                        paramValue: value.id,
                        value,
                      })
                      onFilterChange()
                    }}
                  />
                </Field>
              </Flex.Item>

              {PUR_4252_BUDGET_BY_CUSTOM_FIELDS &&
                collectionId !== 'dashboard.budget.category.list' &&
                customFieldFilterRows.map(renderCustomFieldFilterRow)}
            </Flex.Row>

            <Divider />

            <Flex.Row>
              <Flex.Item>
                <SavedFilterManager.SaveButton onClick={dismiss} />
              </Flex.Item>
              <Flex.Item ml='auto'>
                <TextButton
                  onClick={() => {
                    clearFilters()
                    clearCustomFieldFilterRows()
                    dismiss()
                  }}
                >
                  {intl.formatMessage(messages.clearFilterButtonLabel)}
                </TextButton>
              </Flex.Item>
            </Flex.Row>
          </>
        )}
      </FiltersPopover>
    </BudgetCategoryListSavedFilterManager>
  )
}
